import Sheet from 'components/utils/Sheet'
import React, { useCallback, useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { rem } from 'polished'
import MerchandiseCategoryMegaMenu from '../MerchandiseMegaMenu/MerchandiseCategoryMenu'
import MerchandiseBrandsMegaMenu from '../MerchandiseMegaMenu/MerchandiseBrandsMenu'
import MerchandiseTravelEssentialsMegaMenu from '../MerchandiseMegaMenu/MerchandiseTravelEssentialsMenu'
import { MerchandiseNavBarButton, MerchandiseNavBarFilterChip } from './MerchandiseNavBarButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'
import MerchandiseCategoryDrawer from '../MerchandiseMegaMenu/MerchandiseCategoryDrawer'
import MerchandiseBrandsDrawer from '../MerchandiseMegaMenu/MerchandiseBrandsDrawer'
import MerchandiseTravelEssentialsDrawer from '../MerchandiseMegaMenu/MerchandiseTravelEssentialsDrawer'
import { useTrackedMenuOpen, trackMegaMenuOpen, trackMegaMenuLinkClick, MegaMenuSection } from 'analytics/snowplow/events/megaMenuEvents'

const Bar = styled(Sheet)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};
  padding: 0;

  &.disable-sticky {
    position: relative;
  }

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(20)};
  }
`

const ButtonsContainer = styled.div`
  position: relative;
  display: flex;
  gap: ${rem(4)};
`

const ChipsWrapper = styled.div`
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: ${rem(48)};
    background: linear-gradient(to left, ${props => props.theme.palette.neutral.default.eight}, transparent);
    pointer-events: none;
  }
`

const ChipsContainer = styled.div`
  display: flex;
  gap: ${rem(8)};
  padding: ${rem(12)};
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`

interface DesktopMenuConfig {
  label: string
  section: MegaMenuSection
  Menu: React.ComponentType<{ onClose: () => void }>
  alignDropdownWithButton?: boolean
}

function useDesktopMenus(): Array<DesktopMenuConfig> {
  const intl = useIntl()
  return [
    {
      label: intl.formatMessage({
        id: 'r1+SRfmtY3',
        defaultMessage: 'Shop by Category',
        description: 'Navigation button label for category mega menu',
      }),
      section: 'category',
      Menu: MerchandiseCategoryMegaMenu,
    },
    {
      label: intl.formatMessage({
        id: 'dxqf8TzYae',
        defaultMessage: 'Shop by Brand',
        description: 'Navigation button label for brand mega menu',
      }),
      section: 'brand',
      Menu: MerchandiseBrandsMegaMenu,
    },
    {
      label: intl.formatMessage({
        id: 'qq4JJMZMc3',
        defaultMessage: 'Travel Essentials',
        description: 'Navigation button label for travel essentials mega menu',
      }),
      section: 'travel_essential',
      Menu: MerchandiseTravelEssentialsMegaMenu,
      alignDropdownWithButton: true,
    },
  ]
}

interface DirectLinkConfig {
  label: string
  section: MegaMenuSection
  to: string
}

interface MobileDrawerConfig {
  label: string
  section: MegaMenuSection
  Drawer: React.ComponentType
}

function useDirectLinks(): Array<DirectLinkConfig> {
  const intl = useIntl()
  return [
    {
      label: intl.formatMessage({
        id: 'lbLIE4avbs',
        defaultMessage: 'Free Shipping',
        description: 'Navigation link label for free shipping filter',
      }),
      section: 'free_shipping',
      to: '/search/shop?freeShipping=true',
    },
  ]
}

function useMobileDrawers(): Array<MobileDrawerConfig> {
  const intl = useIntl()
  return [
    {
      label: intl.formatMessage({
        id: '5kTvm803rP',
        defaultMessage: 'Categories',
        description: 'Mobile drawer chip label for category menu',
      }),
      section: 'category',
      Drawer: MerchandiseCategoryDrawer,
    },
    {
      label: intl.formatMessage({
        id: 'Jw3JLXqmkd',
        defaultMessage: 'Brands',
        description: 'Mobile drawer chip label for brand menu',
      }),
      section: 'brand',
      Drawer: MerchandiseBrandsDrawer,
    },
    {
      label: intl.formatMessage({
        id: '7Tnf/We9Bx',
        defaultMessage: 'Travel Essentials',
        description: 'Mobile drawer chip label for travel essentials menu',
      }),
      section: 'travel_essential',
      Drawer: MerchandiseTravelEssentialsDrawer,
    },
  ]
}

function TrackedMenuButton({ label, section, Menu, alignDropdownWithButton }: DesktopMenuConfig) {
  const onOpenChange = useTrackedMenuOpen(section)
  return (
    <MerchandiseNavBarButton
      label={label}
      onOpenChange={onOpenChange}
      alignDropdownWithButton={alignDropdownWithButton}
    >
      {(menuOpen, onClose) => menuOpen && <Menu onClose={onClose} />}
    </MerchandiseNavBarButton>
  )
}

function TrackedLinkButton({ label, section, to }: DirectLinkConfig) {
  const handleClick = useCallback(() => trackMegaMenuLinkClick(section), [section])
  return (
    <MerchandiseNavBarButton
      label={label}
      to={to}
      onClick={handleClick}
      showChevron={false}
    />
  )
}

function TrackedLinkChip({ label, section, to }: DirectLinkConfig) {
  const handleClick = useCallback(() => trackMegaMenuLinkClick(section), [section])
  return (
    <MerchandiseNavBarFilterChip
      label={label}
      to={to}
      onClick={handleClick}
    />
  )
}

function TrackedDrawerChip({ label, section, Drawer }: MobileDrawerConfig) {
  const showModal = useContext(ModalContext)
  const handleClick = useCallback(() => {
    trackMegaMenuOpen(section)
    showModal(<Drawer />)
  }, [section, showModal, Drawer])

  return (
    <MerchandiseNavBarFilterChip
      label={label}
      showChevron
      onClick={handleClick}
    />
  )
}

function MerchandiseNavBar() {
  const barRef = useRef<HTMLDivElement>(null)
  useAppHeaderObserver(barRef, 'sticky')

  const desktopMenus = useDesktopMenus()
  const directLinks = useDirectLinks()
  const mobileDrawers = useMobileDrawers()

  return <Bar background="neutral-eight" ref={barRef}>
    <CSSBreakpoint min="desktop">
      <ButtonsContainer>
        {desktopMenus.map((config) => (
          <TrackedMenuButton key={config.section} {...config} />
        ))}
        {directLinks.map((config) => (
          <TrackedLinkButton key={config.section} {...config} />
        ))}
      </ButtonsContainer>
    </CSSBreakpoint>

    <CSSBreakpoint max="tablet">
      <ChipsWrapper>
        <ChipsContainer>
          {mobileDrawers.map((config) => (
            <TrackedDrawerChip key={config.section} {...config} />
          ))}
          {directLinks.map((config) => (
            <TrackedLinkChip key={config.section} {...config} />
          ))}
        </ChipsContainer>
      </ChipsWrapper>
    </CSSBreakpoint>
  </Bar>
}

export default MerchandiseNavBar
